import type { CampaignStats, RateResult, ReportRateKey } from '../types/reports.types';

export const RATES: Record<
  ReportRateKey,
  { label: string; num: keyof CampaignStats; of: keyof CampaignStats; hint: string }
> = {
  deliveryRate: { label: 'Delivery rate', num: 'delivered', of: 'sent', hint: 'Delivered ÷ sent' },
  bounceRate: { label: 'Bounce rate', num: 'bounced', of: 'sent', hint: 'Bounced ÷ sent' },
  openRate: {
    label: 'Open rate',
    num: 'uniqueOpens',
    of: 'delivered',
    hint: 'Unique opens ÷ delivered',
  },
  clickRate: {
    label: 'Click rate',
    num: 'uniqueClicks',
    of: 'delivered',
    hint: 'Unique clicks ÷ delivered',
  },
  clickToOpenRate: {
    label: 'Click-to-open',
    num: 'uniqueClicks',
    of: 'uniqueOpens',
    hint: 'Unique clicks ÷ unique opens',
  },
  complaintRate: {
    label: 'Complaint rate',
    num: 'complaints',
    of: 'delivered',
    hint: 'Complaints ÷ delivered',
  },
  unsubscribeRate: {
    label: 'Unsubscribe rate',
    num: 'unsubscribes',
    of: 'delivered',
    hint: 'Unsubscribes ÷ delivered',
  },
};

export const OPEN_CAVEAT =
  'Open tracking relies on a tracking pixel. Privacy features and image proxying suppress or inflate opens, so treat this as directional.';

export const HIGHER_IS_BETTER: Record<string, boolean> = {
  delivered: true,
  deliveryRate: true,
  openRate: true,
  clickRate: true,
  complaintRate: false,
  bounceRate: false,
  unsubscribeRate: false,
};

export const THRESHOLDS = {
  complaintRate: {
    limit: 0.001,
    severity: 'critical' as const,
    label: 'Spam-complaint rate over 0.1%',
    why: 'This is the number that gets a sending domain blocked. Above 0.1% mailbox providers begin throttling or rejecting outright.',
  },
  bounceRate: {
    limit: 0.05,
    severity: 'warning' as const,
    label: 'Bounce rate over 5%',
    why: 'A high bounce rate signals stale list data and damages sender reputation for every later send.',
  },
  unsubscribeRate: {
    limit: 0.005,
    severity: 'warning' as const,
    label: 'Unsubscribe rate over 0.5%',
    why: 'Sustained opt-outs at this level usually mean targeting or frequency, not content.',
  },
};

export function rate(key: ReportRateKey, stats: CampaignStats | null | undefined): RateResult | null {
  const def = RATES[key];
  if (!def || !stats) return null;
  const den = Number(stats[def.of]) || 0;
  const num = Number(stats[def.num]) || 0;
  if (!den) return null;
  return { key, label: def.label, hint: def.hint, num, den, value: num / den };
}
